import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import AnimatedText from './AnimatedText';

const rows = [
  { them: 'Posting for the sake of posting', us: 'Content tied to a measurable outcome' },
  { them: 'Trend-chasing, brand-heavy creative', us: 'Platform-native, organic-first' },
  { them: 'Influencers picked by follower count', us: 'Creators picked by performance + fit' },
  { them: 'Reports full of vanity metrics', us: 'Clear insights, learnings, next actions' },
  { them: 'Generic playbook, one-size-fits-all', us: 'Strategy shaped around your audience' },
];

/** Animated X — draws two strokes on view */
const AnimatedX = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <svg
      ref={ref}
      viewBox="0 0 32 32"
      width="28"
      height="28"
      className="shrink-0"
      aria-hidden="true"
    >
      <motion.line
        x1="7" y1="7" x2="25" y2="25"
        stroke="#78716C" strokeWidth="2.5" strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={inView ? { pathLength: 1 } : {}}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />
      <motion.line
        x1="25" y1="7" x2="7" y2="25"
        stroke="#78716C" strokeWidth="2.5" strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={inView ? { pathLength: 1 } : {}}
        transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
      />
    </svg>
  );
};

/** Animated Check — draws single stroke on view */
const AnimatedCheck = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <svg
      ref={ref}
      viewBox="0 0 32 32"
      width="30"
      height="30"
      className="shrink-0"
      aria-hidden="true"
    >
      <motion.path
        d="M6 17 L13 24 L26 9"
        stroke="#AA94FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={inView ? { pathLength: 1 } : {}}
        transition={{ duration: 0.55, ease: [0.2, 0.65, 0.3, 0.9], delay: 0.25 }}
      />
    </svg>
  );
};

/** Left row card — slides in from left, text gets strike-through drawn across */
const ThemRow = ({ text, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -60 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: i * 0.08, ease: [0.2, 0.65, 0.3, 0.9] }}
      className="relative flex items-center gap-4 p-6 sm:p-7 bg-[#F4F4F3] border border-[#1C1917]/10 rounded-2xl"
    >
      <AnimatedX />
      <div className="relative min-w-0">
        <span className="text-base sm:text-lg font-semibold text-[#78716C] leading-snug block">
          {text}
        </span>
        {/* strike-through drawn animation */}
        <motion.span
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.55, delay: i * 0.08 + 0.5, ease: 'easeOut' }}
          className="absolute left-0 right-0 top-1/2 h-[2px] bg-[#78716C]/40 origin-left"
        />
      </div>
    </motion.div>
  );
};

/** Right row card — slides in from right, text gets underline highlight */
const UsRow = ({ text, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 60 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: i * 0.08 + 0.1, ease: [0.2, 0.65, 0.3, 0.9] }}
      whileHover={{ scale: 1.02, y: -2 }}
      className="relative flex items-center gap-4 p-6 sm:p-7 bg-[#1C1917] rounded-2xl overflow-hidden group"
    >
      {/* Orange glow that grows on view */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={inView ? { scale: 1, opacity: 1 } : {}}
        transition={{ duration: 0.9, delay: i * 0.08 + 0.35 }}
        className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#AA94FF]/25 blur-3xl"
      />
      <AnimatedCheck />
      <div className="relative min-w-0">
        <span className="text-base sm:text-lg font-bold text-[#F4F4F3] leading-snug block">
          {text}
        </span>
        {/* underline reveal */}
        <motion.span
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.6, delay: i * 0.08 + 0.55, ease: 'easeOut' }}
          className="absolute left-0 right-0 -bottom-1 h-[2px] bg-[#AA94FF] origin-left"
        />
      </div>
    </motion.div>
  );
};

const Comparison = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  // Center divider line grows as user scrolls
  const dividerHeight = useTransform(scrollYProgress, [0.1, 0.85], ['0%', '100%']);
  // Big VS badge rotates + scales on scroll
  const vsRotate = useTransform(scrollYProgress, [0, 1], [-15, 15]);
  const vsScale = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [0.7, 1.15, 0.9]);

  return (
    <section
      ref={sectionRef}
      data-testid="comparison-section"
      className="py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3] relative"
    >
      <div className="max-w-6xl mx-auto relative">
        {/* Split typography header */}
        <div className="text-center mb-6 sm:mb-8">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#AA94FF] mb-6">
            Why HypeLab
          </p>
          <AnimatedText
            as="h2"
            className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-[#1C1917]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            y={50}
            stagger={0.06}
          >
            Don't settle for less.
          </AnimatedText>
        </div>

        {/* Column titles */}
        <div className="grid grid-cols-2 gap-4 sm:gap-8 items-center mb-8 sm:mb-12 mt-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#78716C]"
          >
            Other agencies
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#AA94FF]"
          >
            HypeLab
          </motion.p>
        </div>

        {/* Comparison rows grid */}
        <div className="relative">
          {/* Center divider line that grows on scroll (desktop only) */}
          <motion.div
            aria-hidden="true"
            style={{ height: dividerHeight }}
            className="hidden sm:block absolute left-1/2 top-0 -translate-x-1/2 w-[2px] bg-gradient-to-b from-transparent via-[#AA94FF] to-transparent origin-top z-10"
          />

          {/* Rotating "VS" badge floating over the divider */}
          <motion.div
            aria-hidden="true"
            style={{ rotate: vsRotate, scale: vsScale }}
            className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-20 h-20 rounded-full bg-[#AA94FF] items-center justify-center shadow-xl"
          >
            <span
              className="text-2xl font-black text-[#F4F4F3] tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              VS
            </span>
          </motion.div>

          <div className="grid grid-cols-2 gap-4 sm:gap-8">
            {/* Left column */}
            <div className="flex flex-col gap-4 sm:gap-6">
              {rows.map((r, i) => (
                <ThemRow key={`them-${i}`} text={r.them} i={i} />
              ))}
            </div>
            {/* Right column */}
            <div className="flex flex-col gap-4 sm:gap-6">
              {rows.map((r, i) => (
                <UsRow key={`us-${i}`} text={r.us} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
